/* eslint-disable complexity */
import { CUTOFF_DAYS_DEFAULT, CUTOFF_DAYS_COVID_VACCINATION } from 'constants/booking'
import { bookingIdToDiseaseId } from 'constants/diseases'

import { useState } from 'react'
import T from 'prop-types'
import { useHistory } from 'react-router-dom'
import { addDays, format } from 'date-fns'
import Confirm from 'components/Confirm'
import TimeTable from 'components/TimeTable'
import ErrorMessage from 'components/ErrorMessage'
import Spinner from 'components/Spinner'
import useSWR from 'swr'
import { getAvailability, createBooking, createVaccination } from 'lib/requests'
import { get } from 'lodash'
import { uppercaseFirst } from 'lib/utils'
import { formatDate } from 'lib/date'
import useSession from 'hooks/useSession'
import Joi from 'joi'
import convertToBookedBy from 'apps/booking/helpers/convertToBookedBy'
import convertToBookedByTest from 'apps/booking/helpers/convertToBookedByTest'

import Step from '../../components/Step'

const formatFullDate = ({ start_time }) => {
  if (!start_time) {
    return null
  }

  const fullDate = uppercaseFirst(formatDate(start_time, 'EEEE d. MMMM'))
  const time = format(start_time, 'HH:mm')

  return `${fullDate}, ${time}`
}

export const validator = () =>
  Joi.object({
    slot_id: Joi.number().required(),
  }).unknown(true)

export default function When({ values, onChange, orgUnit, bookingType }) {
  const { session } = useSession()
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [bookingError, setBookingError] = useState(null)
  const bookingTypeId = bookingType?.id ?? ''

  // Use a different cutoff time for covid vaccine
  const CUTOFF_DAYS =
    bookingTypeId === 'COVID_VACCINE' ? CUTOFF_DAYS_COVID_VACCINATION : CUTOFF_DAYS_DEFAULT

  // Offset start date to ensure > 6 months between dose 2 and 3
  const dose3BookingOffset =
    bookingTypeId === 'COVID_VACCINE' && values?.allowDose3BookingFromDate
      ? values?.allowDose3BookingFromDate
      : null

  const { data } = useSWR(
    [
      format(dose3BookingOffset ? new Date(dose3BookingOffset) : new Date(), 'yyyy-MM-dd'),
      format(
        addDays(dose3BookingOffset ? new Date(dose3BookingOffset) : new Date(), CUTOFF_DAYS),
        'yyyy-MM-dd',
      ),
      orgUnit.slug,
      bookingTypeId,
    ],
    getAvailability,
    { refreshInterval: 10 * 1000 },
  )

  const [confirm, setConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const history = useHistory()

  const { user_id, first_name: s_first_name, last_name: s_last_name } = session
  const { first_name = s_first_name, last_name = s_last_name } =
    get(values, 'details.on_behalf_of') || {}
  const fullName = `${first_name} ${last_name}`

  const handleSelect = (slot) => {
    onChange('slot_id', slot.id)
    setBookingError(null)
    setSelectedSlot(slot)
    setConfirm(true)
  }

  const handleAbort = () => {
    setConfirm(false)
    setBookingError(null)
  }

  const handleConfirm = async () => {
    setBookingError(null)

    if (!values.slot_id) {
      setBookingError('slot_id missing')
      return
    }

    setLoading(true)

    // Various actions based on booking type
    let response

    if (bookingType?.id === 'COVID_VACCINE' || bookingType?.id === 'FLU_VACCINE') {
      response = await handleVaccinationConfirm()
    } else if (bookingType?.id === 'COVID_TEST') {
      response = await handleBookingConfirm()
    } else {
      // No matches (should not happen)
      return
    }

    if (response?.error) {
      setLoading(false)
      setBookingError(response.error)
      return
    }

    // The API will return a 207 if a "on behalf of" booking partially failed
    if (response?.status === 207) {
      history.push(`/${orgUnit.slug}/bestilling-feilet`)
      return
    }

    setTimeout(() => {
      history.push(
        `/${orgUnit.slug}/bekreftelse?s=${values.slot_id}&n=${fullName}&type=${bookingType?.id}`,
      )
    }, 2000)
  }

  // Create a test booking
  const handleBookingConfirm = async () => {
    let postData = {
      phone: values.phone,
      details: values.details,
    }

    // Sorry, some trickery is needed to get things correct for booked by
    const isBookingOnBehalfOf = Boolean(values?.details?.on_behalf_of?.national_id)
    if (isBookingOnBehalfOf) {
      postData = convertToBookedByTest(values)
    }

    // Delete on_behalf_of (we should clean up this soon...)
    delete postData.details.on_behalf_of

    // Create the booking
    const response = await createBooking(values.slot_id, postData)
    return response
  }

  // Create a vaccination
  const handleVaccinationConfirm = async () => {
    // Prepare our data
    let postData = {
      person: {
        ...(values?.person || {}),
        national_id: user_id,
        first_name: s_first_name,
        last_name: s_last_name,
      },
      vaccination: {
        ...(values?.vaccination || {}),
        org_unit: orgUnit.id,
        disease_id: bookingIdToDiseaseId(bookingTypeId),
        vaccination_status: 'NOT_COMPLETED',
      },
      booking_details: {
        slot_id: values.slot_id,
      },
      priority: values?.decidedPriority, // This has been calculated in the previous step (ContactInformation.js)
    }

    // Sorry, some trickery is needed to get things correct for booked by
    const isBookingOnBehalfOf = Boolean(values?.details?.on_behalf_of?.national_id)
    if (isBookingOnBehalfOf) {
      postData = convertToBookedBy({ postData, values })
    }

    // Create the vaccination
    const response = await createVaccination({ data: postData, ouId: orgUnit.id })
    return response
  }

  const location = data && data.find(({ id }) => id === get(values, 'location_id'))

  const selectedDate = selectedSlot ? formatFullDate(selectedSlot) : ''
  const slotFull = get(bookingError, 'status') === 409
  const typeName = get(bookingType, 'title', 'test').toLowerCase()

  return (
    <Step title="Velg ønsket tidspunkt">
      {!data && <Spinner full center box={false} />}
      <TimeTable data={location?.dates || []} onSelect={handleSelect} />

      <Confirm
        title={slotFull ? 'Velg et annet tidspunkt' : selectedDate}
        action={slotFull ? 'Velg et annet tidspunkt' : `Bestill ${typeName}`}
        onAbort={handleAbort}
        onConfirm={slotFull ? handleAbort : handleConfirm}
        show={confirm}
        loading={loading}
        loadingMessage="Bestiller time"
        warning={slotFull}
        disabled={loading}
      >
        {slotFull ? (
          <>
            <strong>{selectedDate}</strong> ble plutselig fullt. Vennligst velg et annet tidspunkt
            og prøv igjen.
          </>
        ) : (
          <>
            {location?.title || 'Teststasjon uten navn'}
            <br />
            For {fullName}
            {bookingError ? (
              <div>
                <br />
                <ErrorMessage error={bookingError} />
              </div>
            ) : null}
          </>
        )}
      </Confirm>
    </Step>
  )
}

When.propTypes = {
  values: T.object.isRequired,
  onChange: T.func.isRequired,
  orgUnit: T.shape({
    id: T.string.isRequired,
    slug: T.string.isRequired,
  }).isRequired,
  bookingType: T.object.isRequired,
}
